import React, { FunctionComponent } from 'react';
import { BookingStateAwareBrandProvider } from 'app/components/OrganizationContext';
import Provider from 'app/containers/rtc/Provider';
import { WebAudioConsumer } from 'app/components/WebAudioContext';
import { DeviceManagerProvider } from './DeviceManagerContext';
import { PeersProvider } from './PeersContext';
import { CallSettingsModalProvider } from 'app/containers/call/CallSettingsModalContext';
import { ReportProblemModalProvider } from 'app/containers/call/ReportProblemModalContext';
import CallDropWarning from 'app/containers/call/CallDropWarning';
import BookingContext from 'app/containers/rtc/BookingContext';
import { BookingQueryProvider } from 'app/containers/rtc/BookingContext/BookingQueryContext';
import PeersAudio from 'app/components/rtc/PeersAudio';
import PeersPlaybackProvider from 'app/containers/rtc/PeersPlaybackContext';
import { RtcNotifierProvider } from 'app/containers/rtc/Notifier';
import RTCRoom from 'app/services/rtc/Room';
import { ChatImageProvider } from 'app/containers/rtc/ChatImageContext';
import DeviceManagerEnabler from 'app/components/rtc/DeviceManagerEnabler';
import DeviceManagerPersistence from 'app/containers/rtc/DeviceManagerPersistence';
import { PasteChatImageProviderInjectDeps as PasteChatImageProvider } from 'app/containers/rtc/PasteChatImageContext';
import VolumeAnalyserProvider from 'app/containers/rtc/VolumeAnalyserContext';
import { CallStateDocumentProvider } from 'app/containers/call/CallStateContext';
import BookingDisplayRoleProvider from 'app/containers/rtc/BookingContext/BookingDisplayRoleProvider';
import IdleDisconnectTrigger from 'app/components/call/IdleDisconnectTrigger';
import { PresenceDocumentProvider } from 'app/containers/rtc/PresenceContext';
import {
  NotesDocumentProvider,
} from 'app/containers/notes/NotesContext';
import { SyncNavigatorProvider } from 'app/containers/call/SyncNavigatorContext';
import { SharedSelectionDocumentProvider } from 'app/components/call/SharedSelectionDocumentContext';
import { EntityStateSyncDocumentProvider } from 'app/containers/rtc/EntityStateSyncContext';
import { ChatDocumentProvider } from 'app/components/chat/ChatDocumentContext';
import { DrawProvider } from 'app/components/rtc/draw/DrawContext';

interface IDeps {
  room?: RTCRoom;
}

export const AllRtcProviders: FunctionComponent<IDeps> = ({ children, room }) =>
  <BookingQueryProvider>
    <BookingContext>
      <BookingStateAwareBrandProvider>
        <RtcNotifierProvider>
          <WebAudioConsumer>{webAudio =>
            <DeviceManagerPersistence>{({ value, update }) =>
              <DeviceManagerProvider
                settings={value}
                updateSettings={update}
                webAudio={webAudio}
              >
                <VolumeAnalyserProvider>
                  <CallSettingsModalProvider>
                    <ReportProblemModalProvider>
                      <PeersProvider>
                        <PeersPlaybackProvider>
                          <Provider room={room}>
                            <CallStateDocumentProvider>
                              <PresenceDocumentProvider>
                                <NotesDocumentProvider>
                                  <SyncNavigatorProvider>
                                    <EntityStateSyncDocumentProvider>
                                      <BookingDisplayRoleProvider>
                                        <CallDropWarning>
                                          <IdleDisconnectTrigger />
                                          <PeersAudio />
                                          <ChatDocumentProvider>
                                            <ChatImageProvider>
                                              <PasteChatImageProvider>
                                                <DeviceManagerEnabler>
                                                  <SharedSelectionDocumentProvider>
                                                    <DrawProvider>
                                                      {children}
                                                    </DrawProvider>
                                                  </SharedSelectionDocumentProvider>
                                                </DeviceManagerEnabler>
                                              </PasteChatImageProvider>
                                            </ChatImageProvider>
                                          </ChatDocumentProvider>
                                        </CallDropWarning>
                                      </BookingDisplayRoleProvider>
                                    </EntityStateSyncDocumentProvider>
                                  </SyncNavigatorProvider>
                                </NotesDocumentProvider>
                              </PresenceDocumentProvider>
                            </CallStateDocumentProvider>
                          </Provider>
                        </PeersPlaybackProvider>
                      </PeersProvider>
                    </ReportProblemModalProvider>
                  </CallSettingsModalProvider>
                </VolumeAnalyserProvider>
              </DeviceManagerProvider>
            }</DeviceManagerPersistence>
          }</WebAudioConsumer>
        </RtcNotifierProvider>
      </BookingStateAwareBrandProvider>
    </BookingContext>
  </BookingQueryProvider>;
